import { Auth } from "./types";
import { loadConfig, isPlatformPortal } from "src/utils/config";
import {
    initAuth,
    logout,
    signUp,
    getUserInfo,
    forgotPassword,
    resetPassword,
    getTermsState,
} from "src/utils/auth";
import { initServerClock, stopServerClock } from "src/utils/serverClock";
import { WebSocketActions } from "src/redux/WebSocket/actions";
import { TopBar } from "src/redux/TopBar/types";
import { TopBarActions } from "src/redux/TopBar/actions";
import { CounterPartysActions } from "src/redux/CounterPartys/actions";
import { ModalActions } from "src/redux/Modal/actions";
import { MdActions } from "src/redux/MarketData/actions";
import { SettingsActions } from "src/redux/Settings/actions";
import { SpinnerActions } from "src/redux/Spinner/actions";
import TermsGate from "src/components/views/TermsGate";

export const AuthActions = {
    /**
     * Initialize the app: load config, check for existing session.
     * Dispatched on page load by the Lander component.
     */
    Init: () => {
        return async (dispatch) => {
            let loginComplete = false;
            try {
                const config = await loadConfig();
                // UI API prefix: /ui (cpty-scoped endpoints on ui-gateway).
                // Other services have their own top-level prefixes.
                const uiApi = config.apiBaseUrl || "/ui";

                dispatch({
                    type: Auth.INIT,
                    result: {
                        apiBaseUrl: uiApi,
                        directoryUrl: uiApi + "/dir",
                        confirmationUrl: uiApi + "/confirmations",
                        eventsWssUrl: config.centrifugoUrl || config.wsBaseUrl || "",
                        marketDataUrl: uiApi + "/md",
                        txUrl: uiApi + "/tx",
                        inventoryUrl: uiApi + "/inventory",
                        availabilityUrl: uiApi + "/availability",
                        settingsUrl: uiApi + "/settings",
                        recallUrl: uiApi + "/recall",
                        positionsUrl: uiApi + "/positions",
                        postTradeUrl: uiApi + "/post-trade",
                        tradeMonitorUrl: "/trade-monitor",
                        adminServiceUrl: "/admin",
                        useClientPool: config.useClientPool || false,
                    },
                });

                const authenticated = await initAuth();

                if (authenticated) {
                    const userInfo = getUserInfo();
                    // CompleteLogin runs the FULL post-login pipeline
                    // (LoggedIn + AfterLoggedIn) for non-platform portals
                    // and dispatches INIT_COMPLETE at the end.
                    await dispatch(
                        AuthActions.CompleteLogin(userInfo)
                    );
                    if (isPlatformPortal()) {
                        dispatch({ type: Auth.APP_READY });
                    }
                    loginComplete = true;
                } else if (isPlatformPortal()) {
                    // Platform portals use Keycloak's hosted login (FIDO2). Send
                    // the browser to the BFF OIDC entry point; it bounces through
                    // Keycloak and returns to the portal root with the session
                    // cookie set. Mark login complete so the custom sign-in form
                    // never renders while the navigation is in flight.
                    loginComplete = true;
                    window.location.href = "/auth/oidc/login";
                    return;
                }
            } catch (error) {
                // Print the full cause chain so the actual originating step
                // (e.g. SettingsActions.InitFeatureFlags) is visible. The
                // outer `error` from LoggedIn / AfterLoggedIn is a wrapper
                // that prepends the failing step label; `error.cause` is the
                // raw SDK / fetch error.
                console.error("Auth init failed:", error);
                let cause = error.cause;
                while (cause) {
                    console.error("  caused by:", cause);
                    cause = cause.cause;
                }
                // App-startup failure (settings fetch, server
                // clock sync, etc.). NOT a credentials problem — distinct from
                // LOGIN_ERROR (wrong password / MFA), which is dispatched
                // by DirectLogin / VerifyMfa themselves. Lander renders a
                // generic "service unavailable" overlay on this flag; the
                // technical detail stays in the console.
                dispatch({ type: Auth.APP_INIT_FAILED });
            } finally {
                dispatch({ type: Auth.IS_READY });
                // Logged-in readiness is the AND of Auth.WS_CONNECTED (from
                // the WebSocket middleware) and Auth.INIT_COMPLETE (from
                // CompleteLogin); the reducer flips appReady when both are
                // in. For non-authenticated users — and for the post-auth
                // failure path — dispatch APP_READY here so the SPA can
                // render either the login screen or the init-error overlay
                // (Lander reads state.Auth.loginError and shows
                // initStepFailureMessage instead of the dashboard).
                if (!loginComplete) {
                    dispatch({ type: Auth.APP_READY });
                }
            }
        };
    },

    SetView: (view) => {
        return { type: Auth.SET_VIEW, view };
    },

    /**
     * Direct login via backend /api/login endpoint (no redirect).
     * Returns the login result which may require MFA.
     */
    DirectLogin: (email, password) => {
        return async (dispatch) => {
            const { directLogin, getUserInfo } = await import("src/utils/auth");
            const result = await directLogin(email, password);

            if (result.mfa_required) {
                // MFA challenge - return the result so the UI can show the code prompt
                return result;
            }

            if (result.mfa_pending) {
                // Hard MFA enforcement (#33): the session is restricted to TOTP
                // enrollment. Do NOT complete login / boot the app - proxied calls
                // would 403. Return so the UI opens the (mandatory) TOTP setup; the
                // app boots after enrollment upgrades the session (page reload).
                return result;
            }

            // Login complete - CompleteLogin handles LoggedIn + AfterLoggedIn
            // for non-admin portals (including clock sync, WebSocket).
            // For admin portal, dispatch APP_READY directly since there's no WebSocket.
            const userInfo = getUserInfo();
            await dispatch(AuthActions.CompleteLogin(userInfo));
            if (isPlatformPortal()) {
                dispatch({ type: Auth.APP_READY });
            }
            return result;
        };
    },

    /**
     * Complete MFA verification step.
     * Resends email + password with the MFA code for a single ROPC call.
     */
    VerifyMfa: (email, password, mfaCode) => {
        return async (dispatch) => {
            const { verifyMfaCode, getUserInfo } = await import("src/utils/auth");
            const result = await verifyMfaCode(email, password, mfaCode);

            // CompleteLogin handles LoggedIn + AfterLoggedIn for non-admin portals
            const userInfo = getUserInfo();
            await dispatch(AuthActions.CompleteLogin(userInfo));
            if (isPlatformPortal()) {
                dispatch({ type: Auth.APP_READY });
            }
            return result;
        };
    },

    /**
     * Complete the login flow after authentication succeeds.
     */
    CompleteLogin: (userInfo) => {
        return async (dispatch) => {
            const user = {
                username: userInfo?.email || userInfo?.username,
                attributes: {
                    email: userInfo?.email,
                    given_name: userInfo?.firstName,
                    family_name: userInfo?.lastName,
                },
            };

            dispatch({ type: Auth.LOGIN, user, userRole: userInfo?.role || "trader" });

            // Restore last view
            const role = localStorage.getItem(TopBar.SET_ROLE);
            if (role && role.length > 0) {
                dispatch(TopBarActions.SetRole(role));
            }
            dispatch(ModalActions.Close());
            dispatch(SpinnerActions.Close());

            // Admin portal users (supervisor/operator) don't have cpty-scoped data.
            // Skip loading counter parties, settings, feature flags, etc.
            if (!isPlatformPortal()) {
                // Consent gate: the gateway refuses every UI request from a
                // user who has not accepted the current terms, so both
                // bootstrap actions below would 403 their way to an app-init
                // failure. Render the terms instead; accepting runs them.
                const terms = getTermsState();
                if (terms.pending) {
                    console.info("[Auth] terms not accepted; showing terms instead of booting");
                    dispatch({
                        type: Auth.TERMS_REQUIRED,
                        version: terms.version,
                        effectiveDate: terms.effectiveDate,
                    });
                    dispatch(
                        ModalActions.Open(TermsGate, {
                            ignoreClickOutside: true,
                            // Closing signs out: nothing behind the gate is
                            // reachable, and somebody who opened the wrong
                            // account needs a way back to the sign-in page.
                            onClose: () => dispatch(AuthActions.Logout()),
                        })
                    );
                    return;
                }

                await dispatch(AuthActions.LoggedIn());
                await dispatch(AuthActions.AfterLoggedIn());
                // Init half of login readiness: the app is fully set up.
                // The reducer flips appReady once the WebSocket half
                // (Auth.WS_CONNECTED) has also arrived, so data-ready
                // never appears while post-login fetches still re-render
                // the UI under the user's cursor.
                dispatch({ type: Auth.INIT_COMPLETE });
            }
        };
    },

    /**
     * Register a new user.
     */
    SignUp: ({ email, password, givenName, familyName }) => {
        return async (dispatch) => {
            await signUp({ email, password, givenName, familyName });
            dispatch({ type: Auth.SIGNUP, signupEmail: email });
        };
    },

    /**
     * Step 1 of forgot-password: ask the gateway to email a reset link.
     * The calling component handles the result. Mirrors the old UI's shape,
     * but hits our BFF endpoint instead of Cognito.
     */
    ForgotPassword: (email) => {
        return () => {
            return forgotPassword(email);
        };
    },

    /**
     * Step 2 of forgot-password: set the new password using the token from the
     * emailed link. The old UI's `code` argument is the reset token here.
     */
    VerifyForgotPassword: (email, token, newPassword) => {
        return () => {
            return resetPassword(email, token, newPassword);
        };
    },

    /**
     * Log out and clear tokens.
     */
    Logout: () => {
        return (dispatch) => {
            dispatch(ModalActions.Close());
            dispatch(SpinnerActions.Close());
            dispatch(SettingsActions.StopFeatureFlagsPolling());
            stopServerClock();
            if (isPlatformPortal()) {
                // Platform portals: hand off to the BFF OIDC logout immediately,
                // WITHOUT dispatching LOGOUT first. Clearing auth state would
                // re-render the app into the default (cpty) view for a frame -
                // a cpty-UI flash - before the browser navigates. The full-page
                // redirect through Keycloak's end-session endpoint back to the
                // hosted admin login unloads the app, so the redux reset is moot.
                window.location.href = "/auth/oidc/logout";
                return;
            }
            dispatch({ type: Auth.LOGOUT });
            logout();
        };
    },

    /**
     * Post-login: fetch counter parties, feature flags, set up WebSocket.
     */
    /**
     * The gateway refused a request because this user has not accepted the
     * current terms. Raise the consent modal, so they can act rather than see
     * a failure they cannot explain.
     *
     * Idempotent: a page with several requests in flight gets several refusals,
     * and they must not stack modals.
     */
    TermsRequired: () => {
        return async (dispatch, getState) => {
            const state = getState();
            // Already asking, or the notice raised by a publish is asking for
            // us. Either way this must not stack a second modal.
            if (state.Auth.termsRequired || state.Auth.termsPublished) return;
            // The version this session signed in with is the one that just went
            // stale, so ask what is current rather than showing the old date.
            const { refreshTermsState } = await import("src/utils/auth");
            const terms = await refreshTermsState();
            dispatch({
                type: Auth.TERMS_REQUIRED,
                version: terms.version,
                effectiveDate: terms.effectiveDate,
            });
            dispatch(
                ModalActions.Open(TermsGate, {
                    ignoreClickOutside: true,
                    onClose: () => dispatch(AuthActions.Logout()),
                })
            );
        };
    },

    LoggedIn: () => {
        return async (dispatch) => {
            // Use the real wall-clock for our own [Auth] timing so the
            // numbers stay meaningful even after initServerClock overrides
            // Date.now globally below.
            const realNow = window.__realDateNow || Date.now.bind(Date);
            const t0 = realNow();
            // Wrap an async step so a thrown error is re-thrown with the
            // step label attached. Without this, V8 doesn't capture the
            // call site across an `await` and the catch up in the
            // bootstrap sees only the SDK-internal stack frame
            // (e.g. `at FbClientCore.ts:215:23`) — useless for finding
            // which step actually broke.
            const timed = async (label, fn) => {
                const s = realNow();
                try {
                    return await fn();
                } catch (error) {
                    const wrapped = new Error(
                        `[Auth.LoggedIn] step "${label}" failed at ${realNow() - s}ms (cum=${realNow() - t0}ms): ${error.message}`,
                        { cause: error });
                    throw wrapped;
                } finally {
                    console.info(`[Auth] ${label}: ${realNow() - s}ms (cum=${realNow() - t0}ms)`);
                }
            };

            await timed("CounterPartysActions.GetAll",
                () => dispatch(CounterPartysActions.GetAll()));

            // Feature flags must load before the UI renders - every gated
            // UI affordance depends on them.
            await timed("SettingsActions.InitFeatureFlags",
                () => dispatch(SettingsActions.InitFeatureFlags()));

            // Sync the browser clock to the server's clock (clock-service).
            // This overrides Date.now() globally so expiry timers, countdowns,
            // and status checks use the server's time frame.
            await timed("initServerClock", async () => {
                await initServerClock();
                console.info("[Auth] Server clock synced. Date.now() =", new Date().toISOString());
            });

            await timed("WebSocketActions.SetupWebSocket",
                () => dispatch(WebSocketActions.SetupWebSocket("events")));
        };
    },

    /**
     * Post-login data loading: exchange rates, user settings, etc.
     */
    AfterLoggedIn: () => {
        return async (dispatch) => {
            const realNow = window.__realDateNow || Date.now.bind(Date);
            const t0 = realNow();
            const timed = async (label, fn) => {
                const s = realNow();
                try {
                    return await fn();
                } catch (error) {
                    const wrapped = new Error(
                        `[Auth.AfterLoggedIn] step "${label}" failed at ${realNow() - s}ms (cum=${realNow() - t0}ms): ${error.message}`,
                        { cause: error });
                    throw wrapped;
                } finally {
                    console.info(`[Auth] ${label}: ${realNow() - s}ms (cum=${realNow() - t0}ms)`);
                }
            };

            await timed("MdActions.GetAllExchangeRates",
                () => dispatch(MdActions.GetAllExchangeRates()));
            await timed("SettingsActions.GetUserSettings",
                () => dispatch(SettingsActions.GetUserSettings()));
            await timed("SettingsActions.GetLenderSettings",
                () => dispatch(SettingsActions.GetLenderSettings()));
            await timed("SettingsActions.GetBorrowerSettings",
                () => dispatch(SettingsActions.GetBorrowerSettings()));
            await timed("SettingsActions.GetPeerCptySettings",
                () => dispatch(SettingsActions.GetPeerCptySettings()));
        };
    },
};
